import { RECOMMENDATION_ATTRIBUTION_CAPABILITY, type RecommendationRequestIdentity, type RecommendationSurface, type RecommendationTrigger, type MealType, type RecommendationAttributionReceipt } from "@batch-and-bowl/shared";
import { recommendationAttributionReceiptSchema, recommendationSignalSchema, recommendationAcceptanceIdentitySchema, type RecommendationSignal, type RecommendationAcceptanceIdentity, type RecommendationChoiceContext } from "@batch-and-bowl/validators";
import { apiFetch } from "./api/client";

export interface RecommendationRequestMetadata {
  identity: RecommendationRequestIdentity;
  expectedActorUserId: string;
}
export interface RecommendationAcceptanceMetadata {
  identity: RecommendationAcceptanceIdentity;
  expectedActorUserId: string;
}

function requestKey(input: unknown, trigger: RecommendationTrigger): string {
  return JSON.stringify({ input, trigger }, (_key, value: unknown) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0));
    }
    return value;
  });
}

/** One open, actor-bound flow. Uncertain retries retain identity; acknowledged flows never switch emitters. */
export class RecommendationAttributionFlow {
  readonly attemptId = crypto.randomUUID();
  private pending?: { key: string; request: RecommendationRequestMetadata };
  private readonly acknowledged = new Set<string>();
  private readonly choices = new WeakMap<object, Map<MealType, { attribution: RecommendationAttributionReceipt; position: number }>>();
  constructor(readonly surface: RecommendationSurface, readonly actorUserId: string) {}

  get legacyEmitterAllowed(): boolean { return this.acknowledged.size === 0; }

  request(input: unknown, trigger: RecommendationTrigger): RecommendationRequestMetadata {
    const key = requestKey(input, trigger);
    if (this.pending?.key === key) return this.pending.request;
    const request = { expectedActorUserId: this.actorUserId, identity: {
      attemptId: this.attemptId, decisionId: crypto.randomUUID(), surface: this.surface, trigger,
    } };
    this.pending = { key, request };
    return request;
  }

  observeResponse(request: RecommendationRequestMetadata, value: unknown, sections: readonly { mealType: MealType; candidates: readonly { id: string }[] }[] = []): boolean {
    if (this.pending?.request !== request || request.expectedActorUserId !== this.actorUserId) return false;
    this.pending = undefined;
    const parsed = recommendationAttributionReceiptSchema.safeParse(value);
    if (!parsed.success || Object.entries(request.identity).some(([key, expected]) => parsed.data[key as keyof RecommendationRequestIdentity] !== expected)) return false;
    this.acknowledged.add(parsed.data.decisionId);
    for (const section of sections) section.candidates.forEach((candidate, index) => {
      const contexts = this.choices.get(candidate) ?? new Map();
      contexts.set(section.mealType, { attribution: parsed.data, position: index + 1 });
      this.choices.set(candidate, contexts);
    });
    return true;
  }

  choice(mealType: MealType, candidate: object) { return this.choices.get(candidate)?.get(mealType); }

  feedbackContext(mealType: MealType, candidate: object): RecommendationChoiceContext {
    const choice = this.choice(mealType, candidate);
    return choice ? { attemptId: choice.attribution.attemptId, decisionId: choice.attribution.decisionId, surface: this.surface, mealType } : { surface: "unknown" };
  }

  acceptance(sections: readonly { mealType: MealType; candidates: readonly { id: string }[] }[], selected: Record<MealType, readonly string[]>): RecommendationAcceptanceMetadata | null {
    const choices: NonNullable<RecommendationAcceptanceIdentity["choices"]> = [];
    for (const mealType of ["breakfast", "lunch", "dinner", "snack"] as const) {
      const section = sections.find(value => value.mealType === mealType);
      for (const recipeId of selected[mealType]) {
        const candidate = section?.candidates.find(value => value.id === recipeId);
        const choice = candidate ? this.choice(mealType, candidate) : undefined;
        if (!choice) return null;
        choices.push({ mealType, recipeId, decisionId: choice.attribution.decisionId });
      }
    }
    const parsed = recommendationAcceptanceIdentitySchema.safeParse({ attemptId: this.attemptId,
      decisionIds: [...new Set(choices.map(choice => choice.decisionId))].sort(), choices,
    });
    return parsed.success ? { identity: parsed.data, expectedActorUserId: this.actorUserId } : null;
  }

  rejectResponse(request: RecommendationRequestMetadata, error: unknown): void {
    const failure = error as { status?: unknown; data?: { details?: { code?: unknown } } } | null;
    if (this.pending?.request !== request || failure?.status !== 409) return;
    if (failure.data?.details?.code === "RECOMMENDATION_RESULT_CHANGED" || failure.data?.details?.code === "RECOMMENDATION_DECISION_CHANGED") this.pending = undefined;
  }

  acknowledges(value: unknown): boolean {
    const parsed = recommendationAttributionReceiptSchema.safeParse(value);
    return parsed.success && parsed.data.attemptId === this.attemptId && parsed.data.surface === this.surface && this.acknowledged.has(parsed.data.decisionId);
  }
}

export function recommendationRequestHeaders(request: RecommendationRequestMetadata): Record<string, string> {
  return { "X-Recommendation-Capability": RECOMMENDATION_ATTRIBUTION_CAPABILITY,
    "X-Recommendation-Request": JSON.stringify(request.identity), "X-Expected-User-Id": request.expectedActorUserId };
}

export function recommendationAcceptanceHeaders(request: RecommendationAcceptanceMetadata): Record<string, string> {
  return { "X-Recommendation-Capability": RECOMMENDATION_ATTRIBUTION_CAPABILITY,
    "X-Recommendation-Acceptance": JSON.stringify(request.identity), "X-Expected-User-Id": request.expectedActorUserId };
}

type SignalSender = (url: string, options: RequestInit) => Promise<unknown>;

/** Bounded best-effort observation relay, separate from the user's planning action. */
export async function relayRecommendationSignal(
  actorUserId: string, input: RecommendationSignal,
  dependencies: { send?: SignalSender; wait?: (ms: number) => Promise<unknown> } = {},
): Promise<boolean> {
  const parsed = recommendationSignalSchema.safeParse(input);
  if (!parsed.success || !actorUserId) return false;
  const send = dependencies.send ?? apiFetch;
  const wait = dependencies.wait ?? (ms => new Promise(resolve => setTimeout(resolve, ms)));
  const options: RequestInit = { method: "POST", body: JSON.stringify(parsed.data), headers: { "X-Expected-User-Id": actorUserId } };
  for (let attempt = 0; attempt < 3; attempt++) {
    try { await send("/users/recommendation-signals", options); return true; }
    catch (error) {
      const status = error && typeof error === "object" && "status" in error ? error.status : 0;
      if (status !== 0 && status !== 408 && status !== 429 && !(typeof status === "number" && status >= 500 && status <= 599)) return false;
      if (attempt < 2) await wait(250 * (attempt + 1));
    }
  }
  return false;
}
